import React, { useEffect, useState } from "react";
import { Button, Card, Form, Input, Typography, Alert, Spin, Modal } from "antd";
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeOutlined, WarningOutlined, ClockCircleOutlined, LaptopOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_API } from "../../axios";
import { setAccessToken, setUserDetails, getUserDetails, isTokenValid } from "../../utils/helper";
import { validateLoginForm, generateCSRFToken, setCSRFToken, generateBackendCSRFToken } from "../../utils/validation";
import { sessionManager } from "../../utils/sessionManager";
import "./login.css";


const { Title, Text } = Typography;

// Photon Logo Component - Based on header implementation
const PhotonLogo = ({ size = 'large' }) => {
  const isLarge = size === 'large';

  return (
    <div className={`photon-logo-container ${isLarge ? 'large' : 'small'}`}>
      <div className="photon-logo-dots">
        <div className="w-2 h-2 bg-red-500 login-rounded-full dot-animation dot-1"></div>
        <div className="w-2 h-2 bg-teal-500 login-rounded-full dot-animation dot-2"></div>
        <div className="w-2 h-2 bg-blue-500 login-rounded-full dot-animation dot-3"></div>
        <div className="w-2 h-2 bg-yellow-500 login-rounded-full dot-animation dot-4"></div>
        <div className="w-2 h-2 bg-purple-500 login-rounded-full dot-animation dot-5"></div>
        <div className="w-2 h-2 bg-pink-500 login-rounded-full dot-animation dot-6"></div>
        <div className="w-2 h-2 bg-green-500 login-rounded-full dot-animation dot-7"></div>
        <div className="w-2 h-2 bg-orange-500 login-rounded-full dot-animation dot-8"></div>
      </div>
      {isLarge && <span className="photon-logo-text">Photon</span>}
    </div>
  );
};

const Login = () => {
  const [loginForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [csrfReady, setCsrfReady] = useState(false);
  const [licenseWarning, setLicenseWarning] = useState(null);
  const [licenseExpired, setLicenseExpired] = useState(false);
  const [contactNumber, setContactNumber] = useState(null);
  const [showForceLoginModal, setShowForceLoginModal] = useState(false);
  const [existingSessionInfo, setExistingSessionInfo] = useState(null);
  const [forceLoginLoading, setForceLoginLoading] = useState(false);

  useEffect(() => {
    // Check if user is already logged in using cookie-based token storage
    const existingUserDetails = getUserDetails();
    const tokenValid = isTokenValid();

    if (existingUserDetails && tokenValid) {
      window.location.href = "/";
    }

    // Generate and set CSRF token
    // const csrfToken = generateCSRFToken();
    // setCSRFToken(csrfToken);

    sessionStorage.removeItem('csrf_token');

    // Fetch CSRF token and mark as ready when complete
    generateBackendCSRFToken()
      .then((response) => {
        setCsrfReady(true);

        // Check for license status in response
        if (response?.data?.license) {
          const license = response.data.license;

          // Check if license is expired
          if (license.expired) {
            setLicenseExpired(true);
            setError(`License Expired: ${license.expiryDate || 'Unknown'}. Please contact IT support.`);
            setCsrfReady(false); // Prevent login
          }
          // Check if license is expiring soon (within 3 days)
          else if (license.showWarning && license.daysUntilExpiry <= 3) {
            setLicenseWarning(`Your license will expire in ${license.daysUntilExpiry} day${license.daysUntilExpiry !== 1 ? 's' : ''} (${license.expiryDate}). Please contact IT support to renew.`);
          }
        }
      })
      .catch((error) => {
        console.error('Failed to fetch CSRF token:', error);

        // Check if error is due to license
        if (error.response?.data?.licenseExpired) {
          setLicenseExpired(true);
          setError(error.response.data.message || 'Your license has expired. Please contact IT support.');
          if (error.response.data.contactNumber) {
            setContactNumber(error.response.data.contactNumber);
          }
        } else if (error.response?.data?.licenseInvalid) {
          setError(error.response.data.message || 'License validation failed. Please contact IT support.');
          if (error.response.data.contactNumber) {
            setContactNumber(error.response.data.contactNumber);
          }
        } else {
          setError('Failed to initialize login. Please refresh the page.');
          setCsrfReady(true); // Still allow login attempt for non-license errors
        }
      });

    // Initialize session manager
    sessionManager.setSessionExpiredHandler(() => {
      // Handle session expiry if needed during login
    });
  }, []);

  const onFinish = () => {
    setLoading(true);
    setError(null);
    setValidationErrors({});

    const formValues = loginForm.getFieldsValue();

    // Validate form data
    const validation = validateLoginForm(formValues);

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      setLoading(false);
      return;
    }

    const { username, password } = validation.sanitizedData;

    // Make login request with sanitized data
    axios.post(BASE_API + "/login", {
      username,
      password: password.toUpperCase(),
    })
      .then(res => {
        const resp_data = res?.data?.data;
        const responseCode = res?.data?.code;

        // Check if active session exists
        if (responseCode === 'ACTIVE_SESSION_EXISTS') {
          setExistingSessionInfo(res.data.existing_session);
          setShowForceLoginModal(true);
          setLoading(false);
          return;
        }

        if (resp_data) {
          // Store user details and token securely
          setUserDetails(resp_data.user_details);
          const defaultExpiration = window.ENV_CONFIG?.TOKEN_EXPIRATION_SECONDS || 86400; // Default 24 hours
          setAccessToken(resp_data.access_token, resp_data.expires_in || defaultExpiration);

          // Check if there was a saved path before login
          const savedPath = sessionStorage.getItem("path_before_login");
          if (savedPath) {
            sessionStorage.removeItem("path_before_login");
            window.location.href = savedPath;
          } else {
            window.location.href = "/";
          }
        }
      })
      .catch(e => {
        console.error("Login error:", e);

        // Provide specific and secure error messages based on response
        let errorMessage = "Unable to connect to server. Please check your connection.";

        // Check for CORS error first
        if (e.message && (e.message.includes('CORS') || e.message.includes('Network Error'))) {
          errorMessage = "CORS Error: The server is blocking requests from this origin. Please contact your administrator to configure CORS settings.";
        } else if (e.response) {
          // Server responded with error status
          const status = e.response.status;

          if (status === 401) {
            // Authentication failure - show backend message
            errorMessage = e.response.data?.message || "Invalid username or password. Please try again.";
          } else if (status === 429) {
            // Rate limiting
            errorMessage = "Too many login attempts. Please try again in a few minutes.";
          } else if (status === 403) {
            // Forbidden - account may be disabled
            errorMessage = "Access denied. Please contact your administrator.";
          } else if (status >= 500) {
            // Server errors
            errorMessage = "Server is currently unavailable. Please try again later.";
          } else if (e.response.data?.message) {
            // Other errors with custom message
            errorMessage = e.response.data.message;
          }
        } else if (e.request) {
          // Request made but no response received - could be CORS or network issue
          if (!e.response && e.config) {
            // Likely a CORS preflight failure
            errorMessage = "CORS Error: Unable to connect to server. The request was blocked. Please ensure the backend allows your origin or contact your administrator.";
          } else {
            errorMessage = "Cannot reach server. Please check your network connection.";
          }
        }

        setError(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleForceLogin = () => {
    setForceLoginLoading(true);
    setError(null);

    const formValues = loginForm.getFieldsValue();
    const { username, password } = formValues;

    axios.post(BASE_API + "/force-login", {
      username,
      password: password.toUpperCase(),
      force: true
    })
      .then(res => {
        const resp_data = res?.data?.data;
        if (resp_data) {
          // Store user details and token securely
          setUserDetails(resp_data.user_details);
          const defaultExpiration = window.ENV_CONFIG?.TOKEN_EXPIRATION_SECONDS || 86400;
          setAccessToken(resp_data.access_token, resp_data.expires_in || defaultExpiration);

          // Close modal
          setShowForceLoginModal(false);

          // Check if there was a saved path before login
          const savedPath = sessionStorage.getItem("path_before_login");
          if (savedPath) {
            sessionStorage.removeItem("path_before_login");
            window.location.href = savedPath;
          } else {
            window.location.href = "/";
          }
        }
      })
      .catch(e => {
        console.error("Force login error:", e);
        setError(e.response?.data?.message || "Force login failed. Please try again.");
        setShowForceLoginModal(false);
      })
      .finally(() => {
        setForceLoginLoading(false);
      });
  };

  const handleCancelForceLogin = () => {
    setShowForceLoginModal(false);
    setExistingSessionInfo(null);
  };

  const formatLoginTime = (loginTime) => {
    if (!loginTime) return 'Unknown';

    const loginDate = new Date(loginTime);
    const now = new Date();
    const diffMs = now - loginDate;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="modern-login-page">
      {/* Left Side - Marketing Content */}
      <div className="marketing-section">
        <div className="marketing-content">
          {/* Logo */}
          <div className="marketing-logo">
            <PhotonLogo size="large" />
          </div>

          {/* Main Heading */}
          <div className="marketing-heading">
            <h1 className="gradient-text">Lightning-fast</h1>
            <h1 className="black-text">medical imaging</h1>
            <h1 className="black-text">that adapts to you</h1>
          </div>

          {/* Subtitle */}
          <p className="marketing-subtitle">
            Built on principles that matter to healthcare professionals
          </p>

          {/* Medical Images Grid */}
          <div className="medical-images-grid">
            <div className="medical-image-placeholder">
              <img src="/assets/login-images/login-img-1.jpg" alt="Medical imaging technology" />
            </div>
            <div className="medical-image-placeholder">
              <img src="/assets/login-images/login-img-2.jpg" alt="Brain scan visualization" />
            </div>
            <div className="medical-image-placeholder">
              <img src="/assets/login-images/login-img-3.jpg" alt="X-ray review" />
            </div>
            <div className="medical-image-placeholder">
              <img src="/assets/login-images/login-img-4.jpg" alt="MRI scan analysis" />
            </div>
            <div className="medical-image-placeholder">
              <img src="/assets/login-images/login-img-5.jpg" alt="Digital collaboration" />
            </div>
            <div className="medical-image-placeholder">
              <img src="/assets/login-images/login-img-6.jpg" alt="MRI scanner" />
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="decorative-elements">
            <div className="dots-pattern dots-pattern-1"></div>
            <div className="dots-pattern dots-pattern-2"></div>
            <div className="wave-shape"></div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="login-section">
        <div className="login-content">
          {/* Login Form */}
          <div className="login-form-container">
            {/* Small Logo */}
            <div className="login-logo">
              <PhotonLogo size="small" />
            </div>

            <Title level={2} className="login-title">
              Sign in to Photon PACS
            </Title>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                className="login-alert"
              />
            )}

            {licenseWarning && !error && (
              <Alert
                message="License Expiring Soon"
                description={licenseWarning}
                type="warning"
                showIcon
                className="login-alert"
              />
            )}

            <Form
              form={loginForm}
              onFinish={onFinish}
              layout="vertical"
              className="modern-login-form"
              requiredMark={false}
            >
              <Form.Item
                name="username"
                label="Username"
                rules={[
                  {
                    required: true,
                    message: "Please input your username",
                  },
                ]}
                validateStatus={validationErrors.username ? "error" : ""}
                help={validationErrors.username?.join(", ")}
              >
                <Input
                  prefix={<UserOutlined className="input-icon" />}
                  placeholder="Enter your username"
                  className="modern-input"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  {
                    required: true,
                    message: "Please input your password",
                  },
                ]}
                validateStatus={validationErrors.password ? "error" : ""}
                help={validationErrors.password?.join(", ")}
              >
                <Input.Password
                  prefix={<LockOutlined className="input-icon" />}
                  placeholder="Enter your password"
                  className="modern-input"
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              <Form.Item className="login-actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  disabled={!csrfReady || loading || licenseExpired}
                  className="gradient-button"
                >
                  {licenseExpired ? "License Expired" : !csrfReady ? "Initializing..." : loading ? "Logging in..." : "Login"}
                </Button>
              </Form.Item>

              <div className="login-footer">
                {contactNumber && (
                  <Text type="secondary">
                    Trouble logging in? <a href={`tel:${contactNumber}`} className="support-link">Contact IT support ({contactNumber})</a>
                  </Text>
                )}
                <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                  Version {process.env.APP_VERSION || 'dev'}
                  {process.env.BUILD_DATE && (() => {
                    const date = new Date(process.env.BUILD_DATE);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    return `_${day}${month}`;
                  })()}
                </Text>
              </div>
            </Form>
          </div>
        </div>
      </div>

      {/* Force Login Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WarningOutlined style={{ color: '#faad14', fontSize: '20px' }} />
            <span>Active Session Detected</span>
          </div>
        }
        open={showForceLoginModal}
        onCancel={handleCancelForceLogin}
        footer={[
          <Button key="cancel" onClick={handleCancelForceLogin}>
            Cancel
          </Button>,
          <Button
            key="force"
            type="primary"
            danger
            loading={forceLoginLoading}
            onClick={handleForceLogin}
          >
            Yes, Logout Other Device
          </Button>,
        ]}
        width={500}
      >
        <div style={{ padding: '16px 0' }}>
          <Alert
            message="You are already logged in from another location"
            description="Do you want to logout from that device and login here?"
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          {existingSessionInfo && (
            <div style={{
              background: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              marginTop: '16px'
            }}>
              <Title level={5} style={{ marginTop: 0, marginBottom: '12px' }}>
                Current Session Details:
              </Title>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LaptopOutlined style={{ color: '#1890ff' }} />
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      Device
                    </Text>
                    <Text strong style={{ fontSize: '13px' }}>
                      {existingSessionInfo.user_agent || 'Unknown device'}
                    </Text>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserOutlined style={{ color: '#52c41a' }} />
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      IP Address
                    </Text>
                    <Text strong style={{ fontSize: '13px' }}>
                      {existingSessionInfo.ip_address || 'Unknown'}
                    </Text>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ClockCircleOutlined style={{ color: '#722ed1' }} />
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      Login Time
                    </Text>
                    <Text strong style={{ fontSize: '13px' }}>
                      {formatLoginTime(existingSessionInfo.login_time)}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Login;
